import { getItem, type ItemSlot } from "@clubap/shared";
import { useEffect, useState } from "react";
import { fetchWardrobe, type WardrobeState } from "../api.js";
import { itemArtOf, type ItemArt } from "../art/itemArt.js";
import type { DirectionalFrames } from "../art/manifest.js";

/**
 * La mochila: lo que tienes, en rejilla, y se pone con un clic.
 *
 * Es una rejilla de dibujos y no una lista de nombres porque un armario se mira, no se
 * lee: con doce ítems, reconocer una gorra por su forma es inmediato y por su nombre no.
 *
 * Lo que lo hace posible es `ItemArt.bbox`. El arte de un ítem se autora sobre el lienzo
 * entero del avatar —para que caiga en su sitio al llevarlo puesto— así que una miniatura
 * del lienzo dejaba la gorra en unos pocos píxeles. Es la razón por la que
 * `AvatarPreview` se negaba a hacer miniaturas; con la caja medida, el recorte enseña el
 * dibujo y el problema desaparece.
 */

/** El lienzo que comparten el avatar y todo dibujo de ítem. */
const CANVAS = { width: 400, height: 512 };

/** El fotograma de frente. Las capas declaran `s`, o un string suelto. */
function frontFrame(frames: DirectionalFrames | undefined): string | null {
  if (!frames) return null;
  if (typeof frames === "string") return frames;
  if (Array.isArray(frames)) return frames[0] ?? null;

  const south = frames.s;
  if (typeof south === "string") return south;
  return south?.[0] ?? null;
}

/**
 * El dibujo recortado a su caja, centrado en la ficha.
 *
 * Se amplía hasta que el lado mayor de la caja llena la ficha, así que un dibujo ancho y
 * uno alto ocupan lo mismo y la rejilla se lee pareja. Sin caja medida se enseña el
 * lienzo entero: sale pequeño, pero no miente sobre lo que hay.
 */
function ItemThumb({ art, name }: { art: ItemArt | undefined; name: string }) {
  const src = frontFrame(art?.idle);
  if (!src) return <span className="itemtile__falta" aria-hidden="true" />;

  const box = art?.bbox;
  if (!box || box.width <= 0 || box.height <= 0) {
    return <img src={src} alt="" className="itemtile__lienzo" />;
  }

  const lado = Math.max(box.width, box.height);

  return (
    <span className="itemtile__ventana">
      <img
        src={src}
        alt=""
        style={{
          position: "absolute",
          width: `${(CANVAS.width / lado) * 100}%`,
          height: "auto",
          left: `${50 - ((box.x + box.width / 2) / lado) * 100}%`,
          top: `${50 - ((box.y + box.height / 2) / lado) * 100}%`,
        }}
        title={name}
      />
    </span>
  );
}

interface Props {
  /**
   * Lo que llevas puesto según el ESTADO DE LA SALA, no según la respuesta HTTP.
   *
   * Importa la distinción: equipar viaja por la sala, y el servidor es la autoridad.
   * Leerlo del estado es lo que impide que esto muestre algo distinto de lo que los
   * demás te ven puesto.
   */
  equipped: readonly string[];
  onEquip: (slot: ItemSlot, itemId: string | null) => void;
  onOpenShop: () => void;
}

export function Wardrobe({ equipped, onEquip, onOpenShop }: Props) {
  /*
   * El inventario lo pide la mochila, y sólo cuando se abre.
   *
   * Llegó a pedirlo la ficha, mientras hubo un contador de "en mochila" que se enseñaba
   * con ésta cerrada. Sin ese contador, pedirlo arriba era traer en cada apertura de
   * carnet una lista que casi nadie mira.
   */
  const [wardrobe, setWardrobe] = useState<WardrobeState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const found = await fetchWardrobe();
        if (!cancelled) setWardrobe(found);
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "No se pudo abrir el inventario.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p className="sheet__error" role="alert">
        {error}
      </p>
    );
  }

  if (!wardrobe) return <p className="sheet__hint">Cargando…</p>;

  const owned = wardrobe.items.filter((entry) => wardrobe.owned.includes(entry.id));

  return (
    <div className="mochila">
      {owned.length === 0 ? (
        <div className="sheet__empty">
          <p>Todavía no tienes nada.</p>
        </div>
      ) : (
        <div className="mochila__rejilla">
          {owned.map((entry) => {
            const slot = getItem(entry.id)?.slot;
            if (!slot) return null;

            const worn = equipped.includes(entry.id);

            return (
              <button
                key={entry.id}
                className={`itemtile${worn ? " itemtile--puesto" : ""}`}
                type="button"
                /*
                 * Un clic pone y otro quita, sobre la misma ficha.
                 *
                 * Dos botones —"Poner" y "Quitar"— gastaban una fila entera por ítem y
                 * obligaban a leer cuál era cuál. Con la ficha entera como botón, el
                 * gesto es señalar lo que quieres, que es lo que uno hace con un armario.
                 */
                onClick={() => onEquip(slot, worn ? null : entry.id)}
                aria-pressed={worn}
                title={worn ? `Quitar ${entry.name}` : `Poner ${entry.name}`}
              >
                <ItemThumb art={itemArtOf(entry.id)} name={entry.name} />
                <span className="itemtile__nombre">{entry.name}</span>
              </button>
            );
          })}
        </div>
      )}

      <button className="btn btn--small mochila__tienda" type="button" onClick={onOpenShop}>
        Ir a la tienda
      </button>

      {/* La única regla del sistema que no se deduce mirando la rejilla. */}
      <p className="sheet__hint">Una ranura admite un ítem: poner uno reemplaza el anterior.</p>
    </div>
  );
}
